import { useEffect, useState } from 'react';
import { Button } from '@/components/shared/button';
import { Card } from '@/components/shared/card';
import { ROUTES } from '@/config/routes';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuthState } from '@/hooks/useAuthState';
import { Loader2 } from 'lucide-react';

const ReadinessAssessment = () => {
  const navigate = useNavigate();
  const { user } = useAuthState();
  const [loading, setLoading] = useState(true);
  const [hasAssessment, setHasAssessment] = useState(false);

  useEffect(() => {
    const fetchAssessment = async () => {
      try {
        if (!user) {
          setLoading(false);
          return;
        }

        const { data, error } = await supabase
          .from('readiness_assessments')
          .select('*')
          .eq('user_id', user.id)
          .eq('mode', 'standard')
          .order('created_at', { ascending: false })
          .limit(1);

        if (error) {
          console.error('Error fetching assessment:', error);
          throw error;
        }
        
        setHasAssessment(!!data?.length);
      } catch (error) {
        console.error('Error fetching assessment:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAssessment();
  }, [user]);

  const handleStartAssessment = () => {
    // Allow users to start without login
    navigate(ROUTES.READINESS_ASSESSMENT.WIZARD);
  };

  const handleViewResults = () => {
    // Navigate directly to the dashboard instead of the wizard with view=results
    navigate(ROUTES.READINESS_ASSESSMENT.DASHBOARD);
  };

  const handleReviewAssessment = () => {
    navigate(`${ROUTES.READINESS_ASSESSMENT.WIZARD}?view=review`);
  };

  if (loading) {
    return (
      <div className="container max-w-4xl mx-auto py-8 px-4 flex justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container max-w-4xl mx-auto py-8 px-4">
      <h1 className="text-3xl font-bold mb-8">AI Readiness Assessment</h1>
      
      <div className="space-y-8">
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">About the Assessment</h2>
          <p className="text-gray-600 mb-6">
            Evaluate your jurisdiction's readiness for AI implementation across seven key dimensions:
          </p>
          <ul className="list-disc list-inside text-gray-600 mb-6 space-y-2">
            <li>Data Inventory & Quality</li>
            <li>IT Infrastructure</li>
            <li>Skills & People</li>
            <li>Policy Framework</li>
            <li>Leadership Support</li>
            <li>Public Buy-in</li>
            <li>Process Maturity</li>
          </ul>
          <p className="text-gray-600 mb-6">
            Get personalized insights and recommendations based on your responses.
          </p>
        </Card>

        {user && hasAssessment ? (
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">View Your Assessment</h2>
            <p className="text-gray-600 mb-6">
              Review your AI readiness assessment results or make updates to your responses.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button onClick={handleViewResults} className="flex-1">
                View Results Dashboard
              </Button>
              <Button onClick={handleReviewAssessment} variant="outline" className="flex-1">
                Review Assessment Choices
              </Button>
            </div>
          </Card>
        ) : (
          <Card className="p-6">
            <h2 className="text-xl font-semibold mb-4">Start Your Assessment</h2>
            <p className="text-gray-600 mb-6">
              Begin with a quick assessment of your readiness for AI-powered permitting. 
              {!user && " Sign in to save your progress and view detailed recommendations."}
            </p>
            <Button onClick={handleStartAssessment}>
              Start Assessment
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ReadinessAssessment;